import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface JsonViewerModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  title: string;
  subtitle?: string;
}

export const JsonViewerModal: React.FC<JsonViewerModalProps> = ({
  open,
  onClose,
  data,
  title,
  subtitle,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = async () => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      enqueueSnackbar('JSON copied to clipboard', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to copy JSON', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
     maxWidth={"md"} slotProps={{ paper: {
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        }
      } }}>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box>
            <IconButton onClick={handleCopy} size="small" sx={{ mr: 1 }}>
              <CopyIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: '#1e1e1e',
          overflow: 'auto',
        }}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            fontSize: '13px',
            fontFamily: '"Fira Code", "Courier New", monospace',
            color: '#d4d4d4',
            lineHeight: 1.6,
            overflowX: 'auto',
            '& .json-key': {
              color: '#9cdcfe',
            },
            '& .json-string': {
              color: '#ce9178',
            },
            '& .json-number': {
              color: '#b5cea8',
            },
            '& .json-boolean': {
              color: '#569cd6',
            },
            '& .json-null': {
              color: '#569cd6',
            },
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCopy} startIcon={<CopyIcon />} variant="outlined">
          Copy JSON
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JsonViewerModal;